import React from "react";
import { graphql, useFragment } from "react-relay";

import { faExternalLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { differenceInDays } from "date-fns";

import { getNextBillingCycleAnchorDate } from "~/billing/utils";
import { useID } from "~/lib/hooks";
import { useIntervalUpdate } from "~/lib/hooks";
import {
	isEventValid,
	RecordIconSwitchEvent,
	useUserHasAccessToScheduledMeetingRecordings,
} from "~/meetingRecorder/RecordIcon";

import type { RowProps } from "./index";

import type { RecordingRow_viewer$key as ViewerKey } from "~/__generated__/RecordingRow_viewer.graphql";
import type { RecordingRowOutOfCreditsWarning_viewer$key as ViewerOutOfCreditsWarningKey } from "~/__generated__/RecordingRowOutOfCreditsWarning_viewer.graphql";

import styles from "./ViewEvent.module.css";

type Props = RowProps & {
	hasAccessToSchedulingMeetingAssistant: boolean;
	viewer: ViewerKey;
};

export function OutOfCreditsWarning({
	viewer: viewerKey,
	withSwitch = false,
}: {
	viewer: ViewerOutOfCreditsWarningKey;
	withSwitch?: boolean;
}) {
	const viewer = useFragment(
		graphql`
			fragment RecordingRowOutOfCreditsWarning_viewer on Viewer {
				hasAdminAccess

				account {
					primaryAdmin {
						fullName
					}
					recordingCredits {
						includedAiMeetingCredits
					}
					billingAccount {
						isSubscriptionActive
						subscriptionState
						subscriptionBillingCycleAnchor
					}
				}
			}
		`,
		viewerKey,
	);

	const { recordingCredits, billingAccount, primaryAdmin } = viewer.account;

	const nextCycleAnchor = React.useMemo(() => {
		return getNextBillingCycleAnchorDate(billingAccount.subscriptionBillingCycleAnchor);
	}, [billingAccount.subscriptionBillingCycleAnchor]);
	const usageResetsDays = nextCycleAnchor ? differenceInDays(nextCycleAnchor, new Date()) : 0;

	if (billingAccount.subscriptionState === "SUBSCRIBED") {
		return (
			<div className={styles.outOfCredits}>
				<FontAwesomeIcon icon={faArrowAltCircleUp} className={styles.outOfCreditUpgrade} />
				<div>
					{recordingCredits.includedAiMeetingCredits > 0 && (
						<div className={styles.outOfCreditsTitle}>
							Your workspace is out of recording credits.{" "}
							{nextCycleAnchor && (
								<>
									Credits will refresh in {usageResetsDays} day{usageResetsDays > 0 && "s"}.
								</>
							)}
						</div>
					)}
					<div className={styles.outOfCreditsSubtitle}>
						{/* eslint-disable-next-line max-len */}
						{withSwitch && "Your intent to record has been saved."} The Note Taker will join the meeting if you have
						credits.{" "}
						{!viewer.hasAdminAccess && <>Contact your workspace admin {primaryAdmin?.fullName} to add more credits.</>}
					</div>
					{viewer.hasAdminAccess && (
						<a
							href="/account/billing/overview/#copilot"
							target="_blank"
							rel="noopener"
							data-test-id="calendar-event-ooc-add-more"
						>
							Add more credits
							<FontAwesomeIcon icon={faExternalLink} className={styles.upgradeMoreIcon} />
						</a>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className={styles.outOfCredits}>
			<FontAwesomeIcon icon={faArrowAltCircleUp} className={styles.outOfCreditUpgrade} />
			<div>
				<div className={styles.outOfCreditsTitle}>Upgrade to unlock recording credits</div>
				<div className={styles.outOfCreditsSubtitle}>
					Our Team plan includes 5 credits/user/month, along with other great features.{" "}
					{withSwitch && "Your intent to record has been saved."}
				</div>
				<a href="/account/billing/plan" target="_blank" rel="noopener" data-test-id="calendar-event-ooc-upgrade">
					Upgrade now
					<FontAwesomeIcon icon={faExternalLink} className={styles.upgradeMoreIcon} />
				</a>
			</div>
		</div>
	);
}

const RecordingRowFragment = graphql`
	fragment RecordingRow_viewer on Viewer {
		remainingMeetingRecordingCredits
		...RecordingRowOutOfCreditsWarning_viewer
	}
`;

export default function RecordingRow({ event, eventData, viewer: viewerKey }: Props) {
	const toggleID = useID();
	const now = Date.now();
	const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;
	const meetingHasPassed = event.start.getTime() < now;

	const viewer = useFragment(RecordingRowFragment, viewerKey);

	useIntervalUpdate(ONE_MINUTE_IN_MILLISECONDS);

	// Similar shouldRender checks as in RecordIconSwitchEvent, just to avoid rendering the row without the switch.
	// Difference is that we want to render element for past valid events in this case.
	const recordIconEvent = meetingHasPassed ? eventData.nextOccurrence : { ...event, start: event.start.toISOString() };
	const userHasAccessToScheduledMeetingRecordings = useUserHasAccessToScheduledMeetingRecordings();
	const futureRecordIconEvent = recordIconEvent?.start && new Date(recordIconEvent.start).getTime() >= now;
	const recordIconEventIsValid = recordIconEvent && isEventValid(recordIconEvent);
	const shouldRender = userHasAccessToScheduledMeetingRecordings && futureRecordIconEvent;

	if (!shouldRender) return null;

	let labelContent = "";

	if (recordIconEventIsValid) {
		if (recordIconEvent.willBeRecorded === "RESTRICTED") {
			labelContent = "Recording this meeting is restricted by your workspace";
		} else if (meetingHasPassed) {
			labelContent = recordIconEvent.willBeRecorded === "YES" ? "Next meeting will be recorded" : "Enable Auto-record";
		} else {
			labelContent = recordIconEvent.willBeRecorded === "YES" ? "This meeting will be recorded" : "Enable Auto-record";
		}
	} else {
		labelContent =
			recordIconEvent.conferenceCalls.length === 0
				? "Recording this event requires a video call"
				: "This event cannot be recorded";
	}

	return (
		<>
			<div className={styles.infoRow}>
				<div className={cx(styles.infoRow__icon, styles.centered)}>
					<RecordIconSwitchEvent
						event={recordIconEvent}
						id={toggleID}
						disabled={!recordIconEventIsValid}
						className={styles.recordIconSwitch}
					/>
				</div>
				<div className={styles.infoRow__text}>
					<label htmlFor={toggleID}>{labelContent}</label>
				</div>
			</div>
			{recordIconEvent.willBeRecorded === "YES" && viewer.remainingMeetingRecordingCredits === 0 && (
				<OutOfCreditsWarning withSwitch viewer={viewer} />
			)}
		</>
	);
}
